import React, { useState } from 'react'

import { useMutation } from '@apollo/client'

import { client } from '../../../../apollo'
import { CREATE_BULK_LICENSE } from '../../../../graphql/enterprisePlan'

type FormState = {
  numberOfLicenses: number
  orgName: string
  planExpirationDate: string
  planStartDate: string
  stripePromoCode: string
}

export default function EnterpriseBulkLicenseForm() {
  const [formData, setFormData] = useState<FormState>({
    numberOfLicenses: 0,
    orgName: '',
    planExpirationDate: '',
    planStartDate: '',
    stripePromoCode: '',
  })

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<boolean>(false)
  const [stripeCouponId, setStripeCouponId] = useState<string | null>(null)

  const [createBulkLicense, { loading }] = useMutation(CREATE_BULK_LICENSE, {
    onCompleted: (data) => {
      const result = data.adminCreateBulkLicenseEnterprisePlan
      if (result.statusCode === 200) {
        setSuccess(true)
        setStripeCouponId(result.stripeCouponId)
      } else {
        setError(result.error || 'An unknown error occurred')
      }
    },
    onError: (err) => {
      setError(err.message || 'Failed to create enterprise plan')
    },
    client,
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: name === 'numberOfLicenses' ? parseInt(value, 10) || 0 : value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    try {
      await createBulkLicense({
        variables: {
          numberOfLicenses: formData.numberOfLicenses,
          orgName: formData.orgName,
          planExpirationDate: formData.planExpirationDate,
          planStartDate: formData.planStartDate,
          stripePromoCode: formData.stripePromoCode,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  return (
    <div className="enterprise-bulk-license-form">
      {success && (
        <div className="success-message">
          Enterprise plan created successfully!
          {stripeCouponId && <p>Stripe Coupon ID: {stripeCouponId}</p>}
          Signup link: https://app.blockpartyapp.com/signup?code=
          {formData.stripePromoCode}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="orgName">
            Organization Name
            <input
              type="text"
              id="orgName"
              name="orgName"
              value={formData.orgName}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="numberOfLicenses">
            Number of Licenses
            <input
              type="number"
              id="numberOfLicenses"
              name="numberOfLicenses"
              value={formData.numberOfLicenses || ''}
              onChange={handleInputChange}
              min="1"
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="planStartDate">
            Plan Start Date
            <input
              type="date"
              id="planStartDate"
              name="planStartDate"
              value={formData.planStartDate}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="planExpirationDate">
            Plan Expiration Date
            <input
              type="date"
              id="planExpirationDate"
              name="planExpirationDate"
              value={formData.planExpirationDate}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <div className="form-group">
          <label htmlFor="stripePromoCode">
            Stripe Promo Code
            <input
              type="text"
              id="stripePromoCode"
              name="stripePromoCode"
              value={formData.stripePromoCode}
              onChange={handleInputChange}
              required
            />
          </label>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Creating...' : 'Create Enterprise Plan'}
        </button>
      </form>
    </div>
  )
}
